import React, { useEffect, useRef, useState } from "react";
import { useSimulation } from "@/context/SimulationContext";
import { useI18n } from "@/context/LanguageContext";
import { Pencil, Check, X } from "lucide-react";

/**
 * Editable hourly-rate pill.
 *
 * Displays the current labour hourly rate (€/H) and, on click, expands into an
 * inline number input so the presenter can adjust the rate WITHOUT leaving the
 * current phase.  The new value is committed to the shared simulation state via
 * updateTrailer({ labor_hourly_rate }) — the same field Phase 1 writes into —
 * so every downstream calculation (Phase 2, Phase 3, Phase 5, PDF) updates in
 * real time.
 *
 * Props:
 *   testId  → optional data-testid override
 */
export default function HourlyRatePill({ testId = "hourly-rate-pill" }) {
  const { state, updateTrailer } = useSimulation();
  const { t } = useI18n();
  const current = Number(state.trailer.labor_hourly_rate) || 12;

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(String(current));
  const inputRef = useRef(null);

  // Keep local draft in sync when external changes happen while not editing
  useEffect(() => {
    if (!editing) setDraft(String(current));
  }, [current, editing]);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const commit = () => {
    const raw = String(draft).replace(",", ".").trim();
    const parsed = parseFloat(raw);
    if (!isNaN(parsed) && parsed >= 0) {
      updateTrailer({ labor_hourly_rate: parsed });
    } else {
      // Reset draft to current on invalid input
      setDraft(String(current));
    }
    setEditing(false);
  };

  const cancel = () => {
    setDraft(String(current));
    setEditing(false);
  };

  const handleKey = (e) => {
    if (e.key === "Enter") { e.preventDefault(); commit(); }
    else if (e.key === "Escape") { e.preventDefault(); cancel(); }
  };

  const label = t("trailer.hourlyRate") || "Valor hora";

  if (editing) {
    return (
      <div
        className="flex items-center gap-1 bg-[color:var(--brand)] text-black rounded-full pl-3 pr-1 py-1 shadow-sm"
        data-testid={`${testId}-editor`}
      >
        <span className="text-[11px] font-mono font-bold tracking-wide select-none">€</span>
        <input
          ref={inputRef}
          type="number"
          step="0.5"
          min="0"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={handleKey}
          aria-label={label}
          data-testid={`${testId}-input`}
          className="w-16 bg-transparent border-0 outline-none text-[11px] font-mono font-bold text-black placeholder-black/50 focus:ring-0 focus:outline-none appearance-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none tabular-num text-center"
        />
        <span className="text-[11px] font-mono font-bold select-none">/H</span>
        <button
          type="button"
          onMouseDown={(e) => { e.preventDefault(); commit(); }}
          data-testid={`${testId}-save`}
          className="ml-0.5 w-6 h-6 rounded-full bg-black/15 hover:bg-black/25 flex items-center justify-center transition-colors"
          aria-label="Guardar"
        >
          <Check className="w-3.5 h-3.5" />
        </button>
        <button
          type="button"
          onMouseDown={(e) => { e.preventDefault(); cancel(); }}
          data-testid={`${testId}-cancel`}
          className="w-6 h-6 rounded-full bg-black/15 hover:bg-black/25 flex items-center justify-center transition-colors"
          aria-label="Cancelar"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      data-testid={testId}
      aria-label={`${label} — clique para editar`}
      title={`${label} — clique para editar`}
      className="group inline-flex items-center gap-1.5 bg-[color:var(--brand)] hover:bg-[color:var(--brand-dim)] text-black text-[11px] font-mono font-bold px-3 py-1 rounded-full tracking-wide transition-colors shadow-sm cursor-pointer"
    >
      <span className="tabular-num">
        € {current.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/H
      </span>
      <Pencil className="w-3 h-3 opacity-60 group-hover:opacity-100 transition-opacity" />
    </button>
  );
}
